import { ClientsideLogger } from "../../../../util/logger/logger"
import { getPubSub, Handler } from "DVWebNodeUtils"
import { loadWebPlayer } from "./loader"
import { WebPlayer, ContentConfig, WebPlayerEvent, PlayerConfig } from 'ATVWebPlayer'
import { defaultView } from "./defaultView"
import { onCapabilities } from "./capabilities"
import { PlayerConfigData, getPlayerConfig } from "./config"

type PlayerEvent = WebPlayerEvent | 'beforePlay' | 'play' | 'stop'

const logger = new ClientsideLogger('DVNodePlayer')
const titleHistory: ContentConfig[] = []

let pubSub = getPubSub()
let playerConfig: PlayerConfig
let hasInitialized: boolean = false

export const _resetPlayer = () => {
  pubSub = getPubSub()
}

const playerEventsToSubscribe = [
  'initialized',
  'closed',
  'playing',
  'pinRequired'
] as WebPlayerEvent[]


function init(player: WebPlayer, playerConfigData: PlayerConfigData) {

  if (!player) {
    logger.fatal('No WebPlayer for Player init')
    return
  }

  playerConfig = getPlayerConfig(playerConfigData)

  playerEventsToSubscribe.forEach((event: WebPlayerEvent) => {
    player.on(event, () => { callAction(event) })
  })


  // add some unique behaviour to asinChanged event,
  // We need to call registered callbacks before pushing
  // the new title to history
  player.on('asinChanged', function (nextAsin) {
    callAction('asinChanged', function () {
      titleHistory.push(formatPlaybackMetadata(nextAsin))
    })
  })

  if (playerConfigData.useDefaultView) {
    pubSub.subscribe('play', defaultView.open)
    pubSub.subscribe('closed', defaultView.close)
  } else {
    logger.debug('Custom view requested - defaults not registered')
  }

  logger.debug('initialize player', playerConfig)
  player.initialize(playerConfig)

}

// Return a promise from any function returned here,
// all queued promises will be resolved before play is called
function runBeforePlay(actionCallback: Handler) {
  pubSub.subscribe('beforePlay', actionCallback)
}

function callAction(event: PlayerEvent, actionCallback?: (player: WebPlayer) => void) {
  const callbacks = pubSub.query(event),
    promises: Promise<any>[] = [],
    promiseDone = function () {
      loadWebPlayer().then((player) => {
        if (typeof actionCallback === 'function') {
          actionCallback(player)
        }
      })
    },
    promiseFail = function () {
      logger.debug('Promise failed', event)
    }

  callbacks.forEach(cb => {
    const callbackResult = cb(titleHistory[titleHistory.length - 1])
    if (callbackResult && typeof callbackResult.then === 'function') {
      promises.push(callbackResult)
    }
  })

  if (!promises.length) {
    promiseDone()
  } else {
    Promise.all(promises).then(promiseDone, promiseFail)
  }
}

function prepare(args: PlayerContentConfig) {
  const callPrepareAction = () => {
    callAction('prepare', callPrepare)
  }
  const callPrepare = (player: WebPlayer) => {
    const contentConfig = formatArgs(args)
    logger.debug('WebPlayer Prepare', playerConfig, contentConfig)
    player.prepare(playerConfig, contentConfig)
  }

  loadWebPlayer().then(callPrepareAction)
}

function play(args: PlayerContentConfig) {
  args && titleHistory.push(formatPlaybackMetadata(args))
  callAction('beforePlay', function () {
    triggerPlayback(args)
  })
}

function triggerPlayback(args: PlayerContentConfig) {
  const playbackConfig = {
    clickstreamRefMarker: args.ref || 'atv_dp_unknown',
    playbackInitiatedTime: new Date().getTime()
  }

  const callPlayAction = () => {
    callAction('play', callPlay)
  }

  const callPlay = (player: WebPlayer) => {
    const contentConfig = formatArgs(args)
    logger.debug('WebPlayer Play', playerConfig, contentConfig, playbackConfig)
    player.play(playerConfig, contentConfig, playbackConfig)
  }
  loadWebPlayer().then(callPlayAction)
}

export type PlayerContentConfig = ContentConfig & {
  lcid?: string
  ref?: string
}
function formatArgs(args: PlayerContentConfig = {} as PlayerContentConfig) {
  const playbackArgs = {
    trailer: args.trailer || false,
    position: args.position || 0,
    videoType: args.videoType,
    live: Boolean(args.lcid),
    asin: args.lcid ? args.lcid : args.asin,
    ref: args.ref,
  } as ContentConfig

  return playbackArgs
}

function formatPlaybackMetadata(args: PlayerContentConfig) {
  const playbackMeta = formatArgs(args)
  // TODO: Add event tracking https://i.amazon.com/issues/AVWebDetailPage-3450
  // playbackMeta.startEpoch = parseInt(`${new Date().getTime() / 1000}`, 10)
  return playbackMeta
}

function stop() {
  const callStopAction = (player: WebPlayer) => {
    logger.debug('initialize player', playerConfig)
    player.initialize(playerConfig)
    callAction('closed')
  }
  callAction('stop', callStopAction)
}

export const onPlayerEvent = (event: string, handler: Handler) => pubSub.subscribe(event, handler)

export type Player = {
  on: typeof onPlayerEvent,
  runBeforePlay: typeof runBeforePlay,
  onCapabilities: typeof onCapabilities,
  prepare: typeof prepare,
  play: typeof play,
  stop: typeof stop
}

const player = {
  on: onPlayerEvent,
  runBeforePlay,
  onCapabilities,
  prepare,
  play,
  stop,
}

export const initializePlayer = (playerConfigData: PlayerConfigData, forceReInitialize?: boolean): Player => {

  if (!hasInitialized || forceReInitialize) {
    hasInitialized = true
    loadWebPlayer().then((player: WebPlayer) => {
      init(player, playerConfigData)
    })
  }

  return player
}
